import {
  WSActions,
  WSAlert,
  WSCard,
  WSDivider,
  WSFlexBox,
  WSIcon,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import React from "react";
import { useHistory } from "react-router-dom";
import { queriesInternalAccounts } from "@query";
import { PATH_TAX_WITHHOLDINGS_SCHEDULE_PAYMENT } from "../paths";
import { NextPaymentInfo } from "./NextPaymentInfo";
import { useGoToTransfer } from "../../Transfer/paths";
import { useModalCancelPendingWithholding } from "../modals/ModalCancelPendingWithholding";
import { useTaxPaymentScheduling } from "../hooks/useTaxPaymentScheduling";
import { selectorAccountBalance } from "../../../shared/selectors/selectorAccountBalance";

type Props = {
  internalAccountId?: string;
};

export const AvailableBalance: React.FC<Props> = props => {
  const history = useHistory();
  const goToTransfer = useGoToTransfer();
  const modalCancelPendingWithholding = useModalCancelPendingWithholding();

  const queryBalance =
    queriesInternalAccounts.getInternalAccountBalance.useQuery(
      { internalAccountId: props.internalAccountId! },
      {
        enabled: !!props.internalAccountId
      }
    );

  const {
    scheduledPayments,
    scheduledPaymentAmount,
    processingPaymentAmount,
    hasScheduledPayment,
    hasProcessingPayment,
    hasScheduledTaxPayment,
    hasProcessingTaxPayment,
    isLoading: isTaxPaymentSchedulingLoading
  } = useTaxPaymentScheduling();

  const balance = selectorAccountBalance(queryBalance.data);
  const availableBalance = balance.available;
  const currentBalance = balance.balance;
  const currency = balance.currency;

  if (!props.internalAccountId) {
    return (
      <WSCard header="Available balance">
        <WSList gap="XL">
          <WSText.Heading1 weight="book" color="gray300">
            $--
          </WSText.Heading1>
          <NextPaymentInfo />
        </WSList>
      </WSCard>
    );
  }

  return (
    <WSCard header="Available balance" shimmer={queryBalance.isInitialLoading}>
      <WSList gap="XL">
        <WSText.Heading1 weight="book" color="gray500">
          {formatMoney(availableBalance, currency)}
        </WSText.Heading1>

        {currentBalance ? (
          <WSFlexBox.CenterY justify="space-between">
            <WSText.ParagraphSm color="gray500">
              Total balance
            </WSText.ParagraphSm>
            <WSText.ParagraphSm color="gray600">
              {formatMoney(currentBalance, currency)}
            </WSText.ParagraphSm>
          </WSFlexBox.CenterY>
        ) : null}

        <WSDivider />

        {hasScheduledPayment ? (
          <>
            <WSText.Paragraph weight="medium">Upcoming</WSText.Paragraph>

            <WSFlexBox.CenterY justify="space-between">
              <WSFlexBox.CenterY gap="XS">
                <WSText.ParagraphSm color="gray500">
                  Scheduled payment
                </WSText.ParagraphSm>
                <WSIcon name="info-circle-fill" size="XS" color="gray500" />
              </WSFlexBox.CenterY>
              <WSText.ParagraphSm color="gray600">
                {formatMoney(scheduledPaymentAmount, currency)}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
          </>
        ) : null}

        {hasProcessingPayment ? (
          <>
            <WSText.Paragraph weight="medium">Processing now</WSText.Paragraph>

            <WSFlexBox.CenterY justify="space-between">
              <WSFlexBox.CenterY gap="XS">
                <WSText.ParagraphSm color="gray500">
                  Pending amounts
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
              <WSText.ParagraphSm color="gray600">
                {formatMoney(processingPaymentAmount, currency)}
              </WSText.ParagraphSm>
            </WSFlexBox.CenterY>
            {hasProcessingTaxPayment ? (
              <WSAlert
                theme="neutral"
                title="Payment processing"
                icon="info-circle"
              >
                No changes or cancellations to scheduled payments can be made
                for this quarter to ensure on-time processing and avoid late
                penalties.
              </WSAlert>
            ) : null}
          </>
        ) : null}

        <WSActions
          alignment="fill"
          buttons={[
            ...(hasProcessingTaxPayment
              ? [
                  {
                    label: "Edit scheduled payment",
                    kind: "Secondary" as const,
                    disabled: true
                  }
                ]
              : hasScheduledTaxPayment
                ? [
                    {
                      label: "Cancel scheduled payment",
                      kind: "Secondary" as const,
                      destructive: true,
                      onClick: () => {
                        const payment = scheduledPayments[0];
                        modalCancelPendingWithholding.open({
                          taxPaymentId: payment.id,
                          amount: Number(payment.amount),
                          currency: payment.currency,
                          date: payment.effectiveAt
                        });
                      }
                    }
                  ]
                : [
                    {
                      label: "Schedule tax payment",
                      kind: "Secondary" as const,
                      loading: isTaxPaymentSchedulingLoading,
                      onClick: () => {
                        history.push(PATH_TAX_WITHHOLDINGS_SCHEDULE_PAYMENT);
                      }
                    }
                  ]),
            {
              label: "Transfer",
              kind: "Primary",
              onClick: () => {
                goToTransfer({
                  balance: "taxWithholdings"
                });
              }
            }
          ]}
        />

        <NextPaymentInfo />
      </WSList>
    </WSCard>
  );
};
